import type { Sample } from '@ctcells/contract'
import { enumLabel, maskPii, t } from '@ctcells/i18n'

import type { Column } from '../../atoms/DataTable.tsx'

/**
 * 작업 «구성» 표의 열 — 작업 추가 창(`NewBatchScreen`)과 작업 상세 서랍(`BatchDetailScreen`)이 **같이 쓴다**
 * (eric 2026-10-22 「작업 선택했을 때 상세 내용이... 작업 추가의 내용이 보이도록 해줘」). 그림 부품은 `batchPlanParts.tsx` 다.
 * ⚠ 컴포넌트가 아닌 것만 둔다 — 컴포넌트와 한 파일에 두면 빠른 새로고침이 그 파일을 통째로 다시 읽는다(`react-refresh/only-export-components`).
 */

/** 레시피 표 한 줄이 가져야 할 칸 — 추가 창은 `RecipeListRow`, 상세는 작업이 고정한 판(`Batch.recipe_version`)을 싣는다 */
export interface RecipePlanRow {
  name: string
  version: number
  /** 등록 시각 표기. 모르면 `—` */
  created_text: string
}

/**
 * 레시피 표의 열 — 이름 · 버전 · 생성. 이름 칸은 부르는 쪽이 준다(추가 창 = 라디오 · 페이지 = 이름 단추 · 상세 = 글자).
 */
export function recipePlanColumns<R extends RecipePlanRow>(name: Column<R>): Column<R>[] {
  return [
    name,
    {
      key: 'version',
      header: t('column.version'),
      nowrap: true,
      cell: (row) => <span className="mono">{`v${String(row.version)}`}</span>,
    },
    {
      key: 'created',
      header: t('column.createdAt'),
      nowrap: true,
      cell: (row) => <span className="muted">{row.created_text}</span>,
    },
  ]
}

/**
 * 칸표의 시료 열 — 시료 번호 · 환자 번호 · 타입 · 단계(추가 창 칸표 · 담은 시료 표와 같은 칸).
 * 🔴 시료 번호 · 환자 번호는 PII 접촉면이다 — `view` 가 없으면 가린다.
 */
export function planSampleColumns(view: boolean): Column<Sample>[] {
  return [
    {
      key: 'sampleId',
      header: t('column.sampleId'),
      nowrap: true,
      cell: (s) => <span className="mono">{maskPii(s.sample_id, view)}</span>,
    },
    {
      key: 'patientId',
      header: t('column.patientId'),
      nowrap: true,
      cell: (s) => <span className="mono">{maskPii(s.patient_id, view)}</span>,
    },
    { key: 'type', header: t('column.sampleType'), cell: (s) => s.sample_type ?? t('mask.none') },
    {
      key: 'stage',
      header: t('column.step'),
      nowrap: true,
      cell: (s) => enumLabel('enum.sampleStage', s.stage),
    },
  ]
}

/**
 * 트레이 칸 안 작은 글씨용 시료 번호 — 칸 폭(그림 단위 72)에 드는 꼴로 뒤쪽만 남긴다(`…140814-09`). 온전한 값은 옆 표가 보인다.
 */
const CAPTION_TAIL = 10
export function shortSampleId(id: string): string {
  return id.length > CAPTION_TAIL + 1 ? `…${id.slice(-CAPTION_TAIL)}` : id
}
